import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  submitContextualPreset,
  pollContextualJob,
  fetchContextualAsset,
  fetchContextualReveal,
  fetchContextualNightContext,
  fetchContextualNightMagnified,
  listPresets,
  listContextualPresets,
} from "@/services/nvgSimulationApi";
import type {
  ContextualCropRegion,
  ContextualJobResult,
  ContextualScenePreset,
  NvgSimulationPresetInfo,
} from "@/types/nvgSimulation";

// Fallback defaults — used only as the initial Select value before the live
// preset lists load. The authoritative keys always come from GET /presets
// and GET /simulate/contextual/presets.
const DEFAULT_SCENE_PRESET = "soldier_gun_1";
const DEFAULT_TUBE_PRESET = "Gen3_Standard";

type Phase = "idle" | "submitting" | "polling" | "loading" | "ready" | "error";
type ViewMode = "nvg" | "night";

export default function NvgContextual() {
  const [scenePreset, setScenePreset] = useState(DEFAULT_SCENE_PRESET);
  const [tubePreset, setTubePreset] = useState(DEFAULT_TUBE_PRESET);
  const { data: scenePresets, isLoading: scenePresetsLoading } = useQuery<
    Record<string, ContextualScenePreset>
  >({
    queryKey: ["nvg-contextual-presets"],
    queryFn: listContextualPresets,
  });
  const { data: tubePresets, isLoading: tubePresetsLoading } = useQuery<
    Record<string, NvgSimulationPresetInfo>
  >({
    queryKey: ["nvg-presets"],
    queryFn: listPresets,
  });
  const scenePresetOptions = scenePresets
    ? Object.entries(scenePresets).map(([key, p]) => ({
        value: key,
        label: p.name,
      }))
    : [];
  const tubePresetOptions = tubePresets
    ? Object.entries(tubePresets).map(([key, p]) => ({
        value: key,
        label: p.name,
      }))
    : [];
  const [phase, setPhase] = useState<Phase>("idle");
  const [pollStatus, setPollStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobResult, setJobResult] = useState<ContextualJobResult | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("nvg");

  // NVG mode: NVG-simulated images + daylight reveal
  const [nvgContextUrl, setNvgContextUrl] = useState<string | null>(null);
  const [nvgMagnifiedUrl, setNvgMagnifiedUrl] = useState<string | null>(null);
  const [revealUrl, setRevealUrl] = useState<string | null>(null);

  // Night mode: nighttime images + NVG magnified as reveal
  const [nightContextUrl, setNightContextUrl] = useState<string | null>(null);
  const [nightMagnifiedUrl, setNightMagnifiedUrl] = useState<string | null>(
    null,
  );

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  async function handleRun() {
    setPhase("submitting");
    setError(null);
    setJobResult(null);
    setNvgContextUrl(null);
    setNvgMagnifiedUrl(null);
    setRevealUrl(null);
    setNightContextUrl(null);
    setNightMagnifiedUrl(null);
    stopPolling();

    let jobId: string;
    try {
      const submitted = await submitContextualPreset(scenePreset, tubePreset);
      jobId = submitted.job_id;
    } catch (e) {
      setPhase("error");
      setError(e instanceof Error ? e.message : "Failed to submit job");
      return;
    }

    setPhase("polling");
    pollRef.current = setInterval(async () => {
      try {
        const status = await pollContextualJob(jobId);
        setPollStatus(status.status);

        if (status.status === "complete" && status.result) {
          stopPolling();
          setJobResult(status.result);
          setPhase("loading");

          const [nvgCtx, nvgMag, rev, nightCtx, nightMag] = await Promise.all([
            fetchContextualAsset(jobId, "context"),
            fetchContextualAsset(jobId, "magnified"),
            fetchContextualReveal(scenePreset).catch(() => null),
            fetchContextualNightContext(scenePreset).catch(() => null),
            fetchContextualNightMagnified(scenePreset).catch(() => null),
          ]);

          setNvgContextUrl(nvgCtx);
          setNvgMagnifiedUrl(nvgMag);
          setRevealUrl(rev);
          setNightContextUrl(nightCtx);
          setNightMagnifiedUrl(nightMag);
          setPhase("ready");
        }
      } catch (e) {
        stopPolling();
        setPhase("error");
        setError(e instanceof Error ? e.message : "Polling failed");
      }
    }, 2000);
  }

  // Wire URLs per view mode:
  // NVG mode  → context=NVG context, magnified=NVG magnified, reveal=daylight
  // Night mode → context=night context, magnified=night magnified, reveal=NVG magnified
  const activeContextUrl = viewMode === "nvg" ? nvgContextUrl : nightContextUrl;
  const activeMagnifiedUrl =
    viewMode === "nvg" ? nvgMagnifiedUrl : nightMagnifiedUrl;
  const activeRevealUrl = viewMode === "nvg" ? revealUrl : nvgMagnifiedUrl;

  const showResult =
    phase === "ready" && activeContextUrl && activeMagnifiedUrl && jobResult;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">Contextual Scene Simulation</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">
              Scene Preset
            </label>
            <Select
              value={scenePreset}
              onValueChange={setScenePreset}
              disabled={scenePresetsLoading}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scenePresetOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">Tube Preset</label>
            <Select
              value={tubePreset}
              onValueChange={setTubePreset}
              disabled={tubePresetsLoading}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tubePresetOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleRun}
            disabled={
              scenePresetsLoading ||
              tubePresetsLoading ||
              phase === "submitting" ||
              phase === "polling" ||
              phase === "loading"
            }
          >
            {phase === "submitting" && "Submitting..."}
            {phase === "polling" && `Running (${pollStatus ?? "..."})`}
            {phase === "loading" && "Loading images..."}
            {(phase === "idle" || phase === "ready" || phase === "error") &&
              "Run Simulation"}
          </Button>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {phase === "ready" && (
        <div className="flex items-center gap-3">
          <Tabs
            value={viewMode}
            onValueChange={(v) => setViewMode(v as ViewMode)}
          >
            <TabsList>
              <TabsTrigger value="nvg">NVG View</TabsTrigger>
              <TabsTrigger value="night" disabled={!nightContextUrl}>
                Night View
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="text-xs text-muted-foreground">
            {viewMode === "nvg"
              ? "NVG-simulated scene. Hover the crop region to reveal the daylight image."
              : "Naked-eye nighttime scene. Hover the crop region to reveal the NVG view."}
          </p>
        </div>
      )}

      {showResult && (
        <ContextualResult
          contextUrl={activeContextUrl!}
          magnifiedUrl={activeMagnifiedUrl!}
          {...(activeRevealUrl != null ? { revealUrl: activeRevealUrl } : {})}
          revealSize={viewMode === "night" ? 120 : 50}
          cropRegion={jobResult!.crop_region}
        />
      )}
    </div>
  );
}

interface ContextualResultProps {
  contextUrl: string;
  magnifiedUrl: string;
  revealUrl?: string;
  revealSize?: number;
  cropRegion: ContextualCropRegion;
}

function ContextualResult({
  contextUrl,
  magnifiedUrl,
  revealUrl,
  revealSize = 50,
  cropRegion,
}: ContextualResultProps) {
  const displaySize = 512;
  const overlaySize = 400;
  const overlayOffsetX = 20;
  const overlayOffsetY = -(overlaySize / 2);

  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayPos, setOverlayPos] = useState({ x: 0, y: 0 });
  const [cropRel, setCropRel] = useState({ x: 0.5, y: 0.5 });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setOverlayPos({ x: e.clientX, y: e.clientY });
    const rect = e.currentTarget.getBoundingClientRect();
    setCropRel({
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    });
  }, []);

  const indicatorSize = revealSize;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Result</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          style={{
            position: "relative",
            display: "inline-block",
            width: `${displaySize}px`,
            height: `${displaySize}px`,
          }}
        >
          <img
            src={contextUrl}
            alt="Context View"
            style={{
              width: `${displaySize}px`,
              height: `${displaySize}px`,
              display: "block",
            }}
          />
          {/* Crop region hitbox */}
          <div
            style={{
              position: "absolute",
              left: `${cropRegion.x * displaySize}px`,
              top: `${cropRegion.y * displaySize}px`,
              width: `${cropRegion.width * displaySize}px`,
              height: `${cropRegion.height * displaySize}px`,
              border: "2px dashed rgba(255, 255, 255, 0.8)",
              boxSizing: "border-box",
              cursor: "crosshair",
              zIndex: 1,
            }}
            onMouseEnter={(e) => {
              setShowOverlay(true);
              setOverlayPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setShowOverlay(false)}
          />
        </div>

        {/* Magnified overlay — fixed, follows cursor */}
        {showOverlay && (
          <div
            style={{
              position: "fixed",
              left: `${overlayPos.x + overlayOffsetX}px`,
              top: `${overlayPos.y + overlayOffsetY}px`,
              width: `${overlaySize}px`,
              height: `${overlaySize}px`,
              border: "2px solid rgba(255, 255, 255, 0.6)",
              borderRadius: "4px",
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: 9999,
              background: "#000",
            }}
          >
            <img
              src={magnifiedUrl}
              alt="Magnified View"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            {revealUrl && (
              <img
                src={revealUrl}
                alt=""
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  pointerEvents: "none",
                  clipPath: `circle(${indicatorSize / 2}px at ${cropRel.x * 100}% ${cropRel.y * 100}%)`,
                }}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
